import type {
  CheckedTableRow,
  CheckedTableValue,
} from '@/components_deprecated'
import { CheckedTable } from '@/components_deprecated'
import differenceWith from 'lodash/differenceWith'
import isEqual from 'lodash/isEqual'
import React, {
  useCallback,
  useEffect,
  useEffectEvent,
  useMemo,
  useRef,
  useState,
} from 'react'
import ScrollBar from 'react-perfect-scrollbar'
import styled from 'styled-components'
import type { UserTypeWithDropdowns } from '../../util'
import {
  createDropdownDomains,
  useDropdownDomainTranslations,
} from '../../util'

const Wrapper = styled.div`
  position: relative;
  width: 100%;
  overflow-x: auto;
`

interface DropdownsSettingsTableProps {
  userTypes: UserTypeWithDropdowns[]

  onChange: (diff: CheckedTableValue[]) => void
}

export const DropdownsSettingsTable: React.FC<DropdownsSettingsTableProps> = ({
  userTypes,

  onChange,
}) => {
  const domainTranslations = useDropdownDomainTranslations()

  const prevUserTypes = useRef<UserTypeWithDropdowns[] | null>(null)
  const initialValues = useRef<CheckedTableValue[]>([])

  const [checkedCollection, setCheckedCollection] = useState<
    CheckedTableValue[]
  >([])

  const domains = useMemo(
    () => createDropdownDomains(domainTranslations),
    [domainTranslations]
  )

  const columns = useMemo(() => userTypes.map(type => type.name), [userTypes])

  const rows = useMemo<CheckedTableRow[]>(() => {
    const rows: CheckedTableRow[] = []

    for (let idx = 0; idx < domains.length; idx++) {
      const domain = domains[idx]
      if (typeof domain === 'undefined') continue

      rows.push({
        type: 'header',
        title: domain.title,
        value: domain.title,
        noStrike: idx === 0,
      })

      for (const [title, dropdown] of Object.entries(domain.dropdowns)) {
        rows.push({
          type: 'regular',
          title,
          value: dropdown,
        })
      }
    }

    return rows
  }, [domains])

  const createCheckedValues = useCallback(
    (types: UserTypeWithDropdowns[]) => {
      const values: CheckedTableValue[] = []

      for (const domain of domains) {
        const dropdowns = Object.values(domain.dropdowns)

        for (const type of types) {
          for (const dropdown of dropdowns) {
            const checked = type.dropdowns.includes(dropdown)

            values.push({
              column: type.id,
              row: dropdown,
              checked,
            })
          }
        }
      }

      return values
    },
    [domains]
  )

  const handleSetCheckedCollection = useEffectEvent(
    (values: CheckedTableValue[]) => {
      setCheckedCollection(values)
    }
  )

  useEffect(() => {
    if (isEqual(prevUserTypes.current, userTypes)) return
    prevUserTypes.current = userTypes

    const values = createCheckedValues(userTypes)
    initialValues.current = values

    handleSetCheckedCollection(values)
  }, [createCheckedValues, userTypes])

  function handleChange(value: CheckedTableValue[]) {
    const diff = differenceWith(value, initialValues.current, isEqual)
    setCheckedCollection(value)

    onChange(diff)
  }

  return (
    <Wrapper>
      <ScrollBar>
        <CheckedTable
          value={checkedCollection}
          columns={columns}
          rows={rows}
          onChange={handleChange}
        />
      </ScrollBar>
    </Wrapper>
  )
}
